import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { FareFamily } from '@shared/models/fare-families.model';
import { FlightSearchPayload } from '@shared/models/flight-search-payload.model';
import {
  FlightSearchResponseAirline,
  FlightSearchResponseItinerary,
  FlightSearchResponseLeg,
  FlightSearchResponseLocation,
  FlightSearchResponseSegment,
} from '@shared/models/flight-search-response.model';
import { SelectedFlight } from '@shared/models/selected-flight.model';
import { FlightFilterService } from '@shared/utils/flight-filter.service';
import * as fromActions from '../actions/flights-search.actions';

export interface AirlinesState extends EntityState<FlightSearchResponseAirline> {}
export interface ItinerariesState extends EntityState<FlightSearchResponseItinerary> {}
export interface LegsState extends EntityState<FlightSearchResponseLeg> {}
export interface SegmentsState extends EntityState<FlightSearchResponseSegment> {}
export interface LocationsState extends EntityState<FlightSearchResponseLocation> {}

export interface FlightSearchState {
  airlines: AirlinesState;
  itineraries: ItinerariesState;
  filteredItineraries: ItinerariesState;
  legs: LegsState;
  segments: SegmentsState;
  locations: LocationsState;
  filters: Array<{ name: string; value: any }>;
  search: FlightSearchPayload;
  searchReferenceId: string;
  selectedFlight: SelectedFlight;
  selectedFareFamily: FareFamily;
  resultsLoaded: boolean;
  fareFamilies: Array<FareFamily>;
  navigateFromTravelSearchPage: boolean;
}

export const airlinesAdapter: EntityAdapter<FlightSearchResponseAirline> = createEntityAdapter();
export const itineraryAdapter: EntityAdapter<FlightSearchResponseItinerary> = createEntityAdapter();
export const legAdapter: EntityAdapter<FlightSearchResponseLeg> = createEntityAdapter();
export const segmentAdapter: EntityAdapter<FlightSearchResponseSegment> = createEntityAdapter();
export const locationAdapter: EntityAdapter<FlightSearchResponseLocation> = createEntityAdapter();

export const initialAirlinesState: AirlinesState = airlinesAdapter.getInitialState();
export const initialItinerariesState: ItinerariesState = itineraryAdapter.getInitialState();
export const initialLegsState: LegsState = legAdapter.getInitialState();
export const initialSegmentsState: SegmentsState = segmentAdapter.getInitialState();
export const initialLocationsState: LocationsState = locationAdapter.getInitialState();

export const initialFlightSearchState: FlightSearchState = {
  airlines: initialAirlinesState,
  itineraries: initialItinerariesState,
  filteredItineraries: initialItinerariesState,
  legs: initialLegsState,
  segments: initialSegmentsState,
  locations: initialLocationsState,
  filters: [],
  search: null,
  searchReferenceId: '',
  selectedFlight: null,
  resultsLoaded: false,
  fareFamilies: null,
  selectedFareFamily: null,
  navigateFromTravelSearchPage: false,
};

export function reducer(state = initialFlightSearchState, action: fromActions.FlightsSearchActions): FlightSearchState {
  switch (action.type) {
    case fromActions.CLEAR_FLIGHTS_SEARCH:
      return initialFlightSearchState;

    case fromActions.RESTORE_FLIGHTS_SUCCESS: {
      const { selectedFlight, search, airlines, locations, searchReferenceId, selectedFareFamily } = action.payload;

      return {
        ...state,
        selectedFlight: selectedFlight ? selectedFlight : state.selectedFlight,
        search: search ? { ...state.search, ...search } : state.search,
        airlines: airlinesAdapter.setAll(airlines, state.airlines),
        locations: locationAdapter.setAll(locations, state.locations),
        searchReferenceId,
        selectedFareFamily,
      };
    }
    case fromActions.CREATE_FLIGHTS_SEARCH:
      return {
        ...state,
        search: {
          ...state.search,
          ...action.payload,
        },
      };

    case fromActions.GET_FLIGHT_SEARCH_RESULTS:
      return {
        ...state,
        filters: [],
        searchReferenceId: '',
        selectedFlight: null,
        resultsLoaded: false,
      };

    case fromActions.GET_FLIGHT_SEARCH_RESULTS_SUCCESS:
      return {
        ...state,
        airlines: airlinesAdapter.setAll(action.payload.airlines, state.airlines),
        itineraries: itineraryAdapter.setAll(action.payload.itineraries, state.itineraries),
        filteredItineraries: itineraryAdapter.setAll(action.payload.itineraries, state.filteredItineraries),
        legs: legAdapter.setAll(action.payload.legs, state.legs),
        segments: segmentAdapter.setAll(action.payload.segments, state.segments),
        locations: locationAdapter.setAll(action.payload.locations, state.locations),
        searchReferenceId: action.payload.search_reference_id,
        resultsLoaded: true,
      };

    case fromActions.GET_FLIGHT_FARE_FAMILIES_SUCCESS: {
      const flightData = { ...state.selectedFlight };
      const token = action.payload.booking_token
        ? action.payload.booking_token
        : state.selectedFlight.itinerary.booking_reference;

      // Updating the booking token with the latest from fare family search, yes it's necessary
      flightData.itinerary = { ...flightData.itinerary, booking_reference: token };

      return {
        ...state,
        selectedFlight: flightData,
        fareFamilies: action.payload.fare_families,
      };
    }
    case fromActions.CLEAR_FLIGHT_FARE_FAMILIES:
      return {
        ...state,
        fareFamilies: initialFlightSearchState.fareFamilies,
        selectedFareFamily: initialFlightSearchState.selectedFareFamily,
      };

    case fromActions.CLEAR_SELECTED_FLIGHT_BOOKING_TOKEN: {
      const selectedFlightData = { ...state.selectedFlight };
      selectedFlightData.itinerary = { ...selectedFlightData.itinerary, booking_reference: '' };

      return {
        ...state,
        selectedFlight: selectedFlightData,
      };
    }
    case fromActions.CLEAR_SELECTED_FLIGHT:
      return {
        ...state,
        selectedFlight: null,
      };
    case fromActions.GET_FLIGHT_SEARCH_RESULTS_FAILURE:
      return {
        ...initialFlightSearchState,
        search: state.search,
        resultsLoaded: true,
      };

    case fromActions.FILTER_FLIGHT_SEARCH_RESULTS: {
      const { filteredLegs, filteredSegments } = FlightFilterService.FilterLegsAndSegments(
        action.payload,
        state.segments.entities,
        state.legs.entities,
        state.itineraries.ids,
        state.itineraries.entities
      );

      const filteredItineraries = FlightFilterService.FilterItineraries(
        action.payload,
        state.itineraries.ids,
        state.itineraries.entities,
        filteredLegs,
        filteredSegments
      );

      return {
        ...state,
        filteredItineraries: itineraryAdapter.setAll(filteredItineraries, state.filteredItineraries),
        filters: action.payload,
      };
    }
    case fromActions.SELECT_FLIGHT:
      return {
        ...state,
        selectedFlight: { ...action.payload, originalToken: action.payload.itinerary.booking_reference },
      };

    case fromActions.SELECT_FLIGHT_FARE_FAMILY:
      return {
        ...state,
        selectedFareFamily: action.payload,
      };

    case fromActions.CHECK_FLIGHTS_SEARCH_CANCEL:
      return {
        ...initialFlightSearchState,
        search: state.search,
      };

    case fromActions.NAVIGATE_FROM_TRAVEL_SEARCH_PAGE:
      return {
        ...state,
        navigateFromTravelSearchPage: action.payload,
      };

    default:
      return state;
  }
}

export const getNumberOfFilteredItineraries = (state: FlightSearchState) => state.filteredItineraries.ids.length;
export const getFilters = (state: FlightSearchState) => state.filters;
export const getSearchReferenceId = (state: FlightSearchState) => state.searchReferenceId;
export const getSearchData = (state: FlightSearchState) => state.search;
export const getSelectedFlight = (state: FlightSearchState) => state.selectedFlight;
export const getSelectedFareFamily = (state: FlightSearchState) => state.selectedFareFamily;
export const getResultsLoaded = (state: FlightSearchState) => state.resultsLoaded;
export const getFareFamilies = (state: FlightSearchState) => state.fareFamilies;
export const getNavigateFromTravelSearchPage = (state: FlightSearchState) => state.navigateFromTravelSearchPage;
